import { useEffect, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { fetchQuery, graphql } from 'relay-runtime';
import type { mostUsedSitesQuery } from '#generated/graphql/mostUsedSitesQuery.graphql';
import {
  rankMostUsedSites,
  type RovoMostUsedSite,
} from './mostUsedSitesRanking';

const query = graphql`
  query mostUsedSitesQuery {
    activity {
      myActivity {
        viewed(
          first: 50
          filter: {
            arguments: {
              products: ["Confluence", "Jira"]
              objectTypes: ["issue", "page", "blogpost"]
            }
          }
        ) {
          edges {
            node {
              object {
                rootContainerId
              }
            }
          }
        }
      }
    }
  }
`;

export function useRovoMostUsedSites(
  enabled: boolean,
): UseRovoMostUsedSitesResult {
  const environment = useRelayEnvironment();
  const [mostUsedSites, setMostUsedSites] = useState<RovoMostUsedSite[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setMostUsedSites([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const subscription = fetchQuery<mostUsedSitesQuery>(
      environment,
      query,
      {},
      { fetchPolicy: 'store-or-network' },
    ).subscribe({
      next: (data) => {
        const edges = data.activity?.myActivity?.viewed.edges ?? [];
        setMostUsedSites(
          rankMostUsedSites(
            edges.map(({ node: { object } }) => object.rootContainerId),
          ),
        );
      },
      error: () => {
        setMostUsedSites([]);
        setIsLoading(false);
      },
      complete: () => {
        setIsLoading(false);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [enabled, environment]);

  return { mostUsedSites, isLoading };
}

export type UseRovoMostUsedSitesResult = {
  mostUsedSites: RovoMostUsedSite[];
  isLoading: boolean;
};

export type { RovoMostUsedSite };
